// Classroom — America's Schoolhouse as a navigable LMS.
//
// Netflix/LMS-style hub over the classroom.json manifest (generated from
// video_pipeline by scripts/generate_classroom_manifest.py, served from the
// CDN). Two rails — the FAQ curriculum (1-14, unpublished lessons render as
// locked coming-soon cards) and Scenario Spotlights — plus search across
// titles/descriptions/transcripts and device-local watch progress.
// Hover previews play a 4s muted 480p micro-clip (~40KB), never the real MP4.
import { SHORTS, SHORTS_PLAYLIST } from "@/data/shorts";
import { liveSocialLinks } from "@/config/socialLinks";
import { ShortsFeed, posterFallback } from "@/components/classroom/ShortsFeed";
import { trackShortsOpen, trackShortsPlay } from "@/utils/analytics";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { Search, Lock, Play, CheckCircle2, GraduationCap, Clock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/contexts/ThemeContext";
import { useClassroomProgress, continueWatchingId } from "@/hooks/useClassroomProgress";

const sans = "'Instrument Sans', system-ui, -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const MANIFEST_URL = "https://static.americanhealthprices.com/data/classroom.json";

export interface Lesson {
  id: string;
  slug: string;
  series: "faq" | "scenario" | "field";
  number: number;
  title: string;
  description: string;
  status: "published" | "coming_soon";
  videoUrl: string | null;
  youtubeUrl: string | null;
  posterUrl: string | null;
  youtubeId?: string | null;
  publishedAt?: string | null;
  previewUrl: string | null;
  articleUrl?: string | null;
  author?: string | null;
  articleTitle?: string | null;
  articleSections?: { heading: string; content: string }[] | null;
  bullets?: string[];
  tags?: string[];
  durationSeconds: number | null;
  chapters: { title: string; start: number }[];
  transcript: { n: number; text: string }[];
}

export interface ClassroomManifest {
  series: Record<string, { title: string; subtitle: string }>;
  lessons: Lesson[];
}

export function useClassroomManifest() {
  return useQuery<ClassroomManifest>({
    queryKey: ["classroom-manifest"],
    queryFn: async () => {
      // A per-minute query string keeps browsers off Cloudflare's cached copy. The
      // Origin-keyed variant of the bare URL sat stale from 2026-09-11 (a lesson
      // published after that stayed "coming soon" for readers while curl looked
      // fine), and the zone token cannot purge it. Queried requests BYPASS the cache.
      const r = await fetch(`${MANIFEST_URL}?v=${Math.floor(Date.now() / 60000)}`);
      if (!r.ok) throw new Error(`classroom.json ${r.status}`);
      return r.json();
    },
    staleTime: 10 * 60 * 1000,
  });
}

export const fmtDur = (s: number | null) => {
  if (!s) return "";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.round(s % 60)).padStart(2, "0")}`;
};

// Strip production tags like "FAQ 4 — " from titles; the card chrome carries №.
export const displayTitle = (l: Lesson) =>
  l.title.replace(/^(FAQ|Ep)\s*\d+\s*—\s*/i, "").replace(/\s*\((lesson, planned|pilot|Origin Episode)\)\s*$/i, "");

function LessonCard({ lesson, progressPct, dark }: { lesson: Lesson; progressPct: number; dark: boolean }) {
  const [hover, setHover] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const locked = lesson.status !== "published";

  const card = (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: "clamp(340px, 27vw, 500px)", flexShrink: 0, borderRadius: 12, overflow: "hidden",
        background: dark ? "#122036" : "#fff",
        border: `1px solid ${dark ? "#25364f" : "#ddd5c4"}`,
        opacity: locked ? 0.75 : 1,
        transition: "transform .18s ease, box-shadow .18s ease",
        transform: hover && !locked ? "translateY(-3px)" : "none",
        boxShadow: hover && !locked ? "0 10px 24px rgba(0,0,0,.18)" : "0 1px 3px rgba(0,0,0,.08)",
      }}
    >
      <div style={{ position: "relative", aspectRatio: "16/9", background: "#0d1420" }}>
        {lesson.posterUrl && (
          <img
            src={lesson.posterUrl}
            alt={displayTitle(lesson)}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        {hover && !locked && lesson.previewUrl && (
          <video
            ref={videoRef}
            src={lesson.previewUrl}
            muted
            loop
            autoPlay
            playsInline
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <span style={{
          position: "absolute", top: 8, left: 8, fontFamily: sans, fontSize: 11, fontWeight: 700,
          letterSpacing: ".06em", background: "rgba(15,26,46,.85)", color: "#C5A55A",
          padding: "3px 8px", borderRadius: 6,
        }}>
          {lesson.series === "faq" ? `CLASS ${lesson.number}` : lesson.series === "field" ? `REPORT ${lesson.number}` : "SPOTLIGHT"}
        </span>
        {locked ? (
          <span style={{
            position: "absolute", top: 8, right: 8, display: "inline-flex", alignItems: "center", gap: 4,
            fontFamily: sans, fontSize: 11, fontWeight: 600, background: "rgba(15,26,46,.85)",
            color: "#cdd6e4", padding: "3px 8px", borderRadius: 6,
          }}>
            <Lock size={11} /> Coming soon
          </span>
        ) : lesson.durationSeconds ? (
          <span style={{
            position: "absolute", bottom: progressPct > 0 ? 12 : 8, right: 8, display: "inline-flex",
            alignItems: "center", gap: 4, fontFamily: sans, fontSize: 11, fontWeight: 600,
            background: "rgba(15,26,46,.85)", color: "#fff", padding: "3px 8px", borderRadius: 6,
          }}>
            <Clock size={11} /> {fmtDur(lesson.durationSeconds)}
          </span>
        ) : null}
        {progressPct >= 90 && (
          <span style={{ position: "absolute", bottom: 12, left: 8, color: "#4ade80" }}>
            <CheckCircle2 size={18} />
          </span>
        )}
        {progressPct > 0 && (
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 4, background: "rgba(255,255,255,.25)" }}>
            <div style={{ width: `${Math.min(progressPct, 100)}%`, height: "100%", background: "#C5A55A" }} />
          </div>
        )}
      </div>
      <div style={{ padding: "12px 16px 16px" }}>
        <div style={{
          fontFamily: sans, fontWeight: 700, fontSize: 17, lineHeight: 1.3,
          color: dark ? "#e9eef6" : "#1f3a5f",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {displayTitle(lesson)}
        </div>
        <div style={{
          fontFamily: sans, fontSize: 14, marginTop: 6, color: dark ? "#9fb0c8" : "#6b6250",
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {lesson.description}
        </div>
      </div>
    </div>
  );

  return locked ? card : (
    <Link to={`/classroom/${lesson.slug}`} style={{ textDecoration: "none" }}>{card}</Link>
  );
}

function Rail({ title, subtitle, children, dark }: { title: string; subtitle?: string; children: React.ReactNode; dark: boolean }) {
  return (
    <section style={{ marginTop: 34 }}>
      <h2 style={{ fontFamily: serif, fontSize: 24, margin: 0, color: dark ? "#e9eef6" : "#1f3a5f" }}>{title}</h2>
      {subtitle && (
        <p style={{ fontFamily: sans, fontSize: 13.5, margin: "4px 0 0", color: dark ? "#9fb0c8" : "#6b6250", maxWidth: "70ch" }}>
          {subtitle}
        </p>
      )}
      <div style={{ display: "flex", gap: 14, overflowX: "auto", padding: "14px 4px 8px", scrollbarWidth: "thin" }}>
        {children}
      </div>
    </section>
  );
}

export default function Classroom() {
  const { t } = useTranslation("common");
  const { isDark } = useTheme();
  const { data, isLoading } = useClassroomManifest();
  const [q, setQ] = useState("");
  const { progress } = useClassroomProgress();

  const lessons: Lesson[] = data?.lessons ?? [];
  const query = q.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!query) return lessons;
    return lessons.filter((l) =>
      l.title.toLowerCase().includes(query) ||
      l.description.toLowerCase().includes(query) ||
      l.transcript.some((line) => line.text.toLowerCase().includes(query))
    );
  }, [lessons, query]);

  const faq = filtered.filter((l) => l.series === "faq").sort((a, b) => a.number - b.number);
  const scenarios = filtered.filter((l) => l.series === "scenario").sort((a, b) => a.number - b.number);
  const fieldReports = filtered.filter((l) => l.series === "field").sort((a, b) => a.number - b.number);

  const contId = continueWatchingId(progress);
  const cont = lessons.find((l) => l.id === contId && l.status === "published");
  const hero = cont ?? lessons.find((l) => l.id === "faq01") ?? lessons.find((l) => l.status === "published");
  const faqDone = lessons.filter((l) => l.series === "faq" && (progress[l.id]?.pct ?? 0) >= 90).length;
  const faqPublished = lessons.filter((l) => l.series === "faq" && l.status === "published").length;

  const bg = isDark ? "#0a1628" : "#f6f1e5";
  const ink = isDark ? "#e9eef6" : "#241f18";

  return (
    <div style={{ minHeight: "100vh", background: bg, color: ink, paddingBottom: 90 }}>
      <Helmet>
        <title>Classroom — America's Schoolhouse | American Hospital Prices</title>
        <meta name="description" content="Free video lessons on American hospital prices: the FAQ series, scenario spotlights, transcripts, and the data to check any hospital yourself. No account needed." />
        <link rel="canonical" href="https://americanhospitalprices.com/classroom" />
      </Helmet>

      <div style={{ width: "100%", padding: "26px clamp(16px, 3vw, 52px) 0", display: "flex", gap: 28, alignItems: "flex-start" }}>
        {/* Left column — everything the page always had */}
        <div style={{ flex: 1, minWidth: 0 }}>
        {/* Masthead */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <GraduationCap size={30} color="#C5A55A" />
          <div>
            <h1 style={{ fontFamily: serif, fontSize: "clamp(26px,4.5vw,38px)", margin: 0, color: isDark ? "#e9eef6" : "#1f3a5f" }}>
              {t("nav.classroom", { defaultValue: "Classroom" })}
            </h1>
            <p style={{ fontFamily: sans, fontSize: 14, margin: 0, color: isDark ? "#9fb0c8" : "#6b6250" }}>
              America's Schoolhouse — free lessons from the hospitals' own published prices. Progress saved on this device.
            </p>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {liveSocialLinks().map((soc) => (
              <a key={soc.id} href={soc.href} target="_blank" rel="noopener noreferrer"
                 title={`${soc.label} · ${soc.handle}`}
                 style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px",
                          borderRadius: 10, textDecoration: "none", fontFamily: sans, fontSize: 12.5,
                          fontWeight: 700, letterSpacing: 0.2,
                          border: `1.5px solid ${isDark ? "#25364f" : "#ddd5c4"}`,
                          background: isDark ? "#122036" : "#fff", color: soc.color === "#000000" && isDark ? "#fff" : soc.color }}>
                <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" aria-hidden="true"><path d={soc.path} /></svg>
                Follow
              </a>
            ))}
          </div>
          <div style={{ position: "relative", minWidth: 240, flex: "0 1 320px" }}>
            <Search size={16} style={{ position: "absolute", left: 12, top: 11, color: "#8a8371" }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("classroom.searchPlaceholder", { defaultValue: "Search lessons & transcripts…" })}
              style={{
                width: "100%", fontFamily: sans, fontSize: 14, padding: "9px 12px 9px 34px",
                borderRadius: 10, border: `1.5px solid ${isDark ? "#25364f" : "#ddd5c4"}`,
                background: isDark ? "#122036" : "#fff", color: ink, outline: "none",
              }}
            />
          </div>
        </div>

        {/* Hero — continue watching or start the series */}
        {hero && !query && (
          <Link to={`/classroom/${hero.slug}`} style={{ textDecoration: "none" }}>
            <div style={{
              marginTop: 22, display: "flex", gap: 20, alignItems: "stretch", borderRadius: 14,
              overflow: "hidden", background: isDark ? "#122036" : "#1f3a5f",
              border: `1px solid ${isDark ? "#25364f" : "#1f3a5f"}`, flexWrap: "wrap",
            }}>
              <div style={{ flex: "1 1 420px", minWidth: 300, position: "relative" }}>
                {hero.posterUrl && (
                  <img src={hero.posterUrl} alt={displayTitle(hero)} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", aspectRatio: "16/9" }} />
                )}
                <span style={{
                  position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{
                    width: 64, height: 64, borderRadius: "50%", background: "rgba(197,165,90,.92)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Play size={28} color="#0f1a2e" style={{ marginLeft: 3 }} />
                  </span>
                </span>
              </div>
              <div style={{ flex: "1 1 320px", padding: "22px 24px", color: "#f2ecdd", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, letterSpacing: ".08em", color: "#C5A55A" }}>
                  {cont ? "CONTINUE WATCHING" : "START HERE"}
                </div>
                <div style={{ fontFamily: serif, fontSize: 26, margin: "6px 0" }}>{displayTitle(hero)}</div>
                <div style={{ fontFamily: sans, fontSize: 14, color: "#cdd6e4", maxWidth: "60ch" }}>{hero.description}</div>
                {cont && progress[hero.id] && (
                  <div style={{ marginTop: 12, height: 5, borderRadius: 3, background: "rgba(255,255,255,.2)", maxWidth: 320 }}>
                    <div style={{ width: `${progress[hero.id].pct}%`, height: "100%", borderRadius: 3, background: "#C5A55A" }} />
                  </div>
                )}
              </div>
            </div>
          </Link>
        )}

        {/* FAQ curriculum */}
        <Rail
          dark={isDark}
          title={data?.series?.faq?.title ?? "FAQ, Finally Answered"}
          subtitle={`${data?.series?.faq?.subtitle ?? ""}${faqPublished ? `  ·  ${faqDone}/${faqPublished} completed on this device` : ""}`}
        >
          {faq.map((l) => (
            <LessonCard key={l.id} lesson={l} progressPct={progress[l.id]?.pct ?? 0} dark={isDark} />
          ))}
          {isLoading && <div style={{ fontFamily: sans, padding: 30 }}>Loading lessons…</div>}
        </Rail>

        {/* Scenario spotlights */}
        {scenarios.length > 0 && (
          <Rail dark={isDark} title={data?.series?.scenario?.title ?? "Scenario Spotlights"} subtitle={data?.series?.scenario?.subtitle}>
            {scenarios.map((l) => (
              <LessonCard key={l.id} lesson={l} progressPct={progress[l.id]?.pct ?? 0} dark={isDark} />
            ))}
          </Rail>
        )}

        {/* Field Reports — data investigations off the blog */}
        {fieldReports.length > 0 && (
          <Rail dark={isDark} title={data?.series?.field?.title ?? "Field Reports"} subtitle={data?.series?.field?.subtitle}>
            {fieldReports.map((l) => (
              <LessonCard key={l.id} lesson={l} progressPct={progress[l.id]?.pct ?? 0} dark={isDark} />
            ))}
          </Rail>
        )}

        {/* Short Format Content — the vertical clips, embedded from our CDN with
            per-video platform links. TikTok per-video buttons light up as post
            URLs are filled into src/data/shorts.ts (posted by hand — ids unknown). */}
        {!query && SHORTS.length > 0 && (
          <div className="xl:hidden">
          <Rail dark={isDark} title="Short Format Content"
                subtitle="60-second price checks — one procedure at a time. Follow on YouTube and TikTok.">
            {SHORTS.map((sc) => (
              <div key={sc.id} style={{ width: 200, flex: "0 0 auto", borderRadius: 14, overflow: "hidden",
                    border: `1.5px solid ${isDark ? "#25364f" : "#ddd5c4"}`, background: isDark ? "#0f1b2e" : "#fff" }}>
                {sc.mp4 ? (
                  <video src={sc.mp4} poster={sc.poster} preload="none" controls playsInline
                         style={{ width: "100%", aspectRatio: "9/16", display: "block", background: "#0a1628" }} />
                ) : (
                  <a href={sc.youtubeUrl} target="_blank" rel="noopener noreferrer" title="Watch on YouTube">
                    <img src={sc.poster} alt={sc.title} loading="lazy" onError={posterFallback}
                         style={{ width: "100%", aspectRatio: "9/16", objectFit: "cover", display: "block", background: "#0a1628" }} />
                  </a>
                )}
                <div style={{ padding: "8px 10px 10px" }}>
                  <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, lineHeight: 1.25, color: isDark ? "#f0ebe3" : "#1b1b1b" }}>
                    {sc.title}
                  </div>
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    <a href={sc.youtubeUrl} target="_blank" rel="noopener noreferrer"
                       style={{ flex: 1, textAlign: "center", fontFamily: sans, fontSize: 11, fontWeight: 700,
                                padding: "5px 0", borderRadius: 8, textDecoration: "none",
                                background: "#FF0000", color: "#fff" }}>YouTube</a>
                    {sc.tiktokUrl ? (
                      <a href={sc.tiktokUrl} target="_blank" rel="noopener noreferrer"
                         style={{ flex: 1, textAlign: "center", fontFamily: sans, fontSize: 11, fontWeight: 700,
                                  padding: "5px 0", borderRadius: 8, textDecoration: "none",
                                  background: isDark ? "#fff" : "#000", color: isDark ? "#000" : "#fff" }}>TikTok</a>
                    ) : (
                      <a href="https://www.tiktok.com/@americanhospitalprices" target="_blank" rel="noopener noreferrer"
                         title="Our TikTok — this clip's direct post link coming soon"
                         style={{ flex: 1, textAlign: "center", fontFamily: sans, fontSize: 11, fontWeight: 700,
                                  padding: "5px 0", borderRadius: 8, textDecoration: "none", opacity: 0.75,
                                  border: `1.5px solid ${isDark ? "#3a4b66" : "#c9c0ac"}`,
                                  color: isDark ? "#f0ebe3" : "#1b1b1b" }}>TikTok ↗</a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Rail>
          </div>
        )}

        {query && filtered.length === 0 && (
          <p style={{ fontFamily: sans, marginTop: 40, color: isDark ? "#9fb0c8" : "#6b6250" }}>
            No lessons match "{q}" yet — more classes air weekly.
          </p>
        )}
        </div>

        {/* Right rail (desktop ≥ xl): the phone-shaped, snap-scrolling Shorts feed.
            Our muted MP4 previews autoplay as you scroll; "Watch on YouTube" swaps
            in the YouTube player (user-pressed play = credited view); TikTok is a
            click-through. See src/components/classroom/ShortsFeed.tsx. */}
        {SHORTS.length > 0 && (
          <aside className="hidden xl:block" style={{ flex: "0 0 clamp(360px, 28vw, 520px)", width: "clamp(360px, 28vw, 520px)", alignSelf: "stretch" }} aria-label="Shorts feed">
            <ShortsFeed
              clips={SHORTS}
              dark={isDark}
              onOpen={(platform, clip) => trackShortsOpen(platform, clip.id, clip.code)}
              onPlay={(clip) => trackShortsPlay(clip.id, clip.code)}
            />
          </aside>
        )}
      </div>
    </div>
  );
}
